import React, { createContext, useCallback, useContext, useState } from 'react';
import type { ProductDto } from '../types/product';

export interface CartItem {
  product: ProductDto;
  quantity: number;
}

interface CartContextValue {
  cartItems: CartItem[];
  cartCount: number;
  addToCart: (products: ProductDto[]) => void;
  removeFromCart: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  const addToCart = useCallback((products: ProductDto[]) => {
    setCartItems((prev) => {
      const existingIds = new Set(prev.map((ci) => ci.product.id));
      const newItems = products
        .filter((p) => !existingIds.has(p.id))
        .map((p) => ({ product: p, quantity: 1 }));
      return [...prev, ...newItems];
    });
  }, []);

  const removeFromCart = useCallback((id: number) => {
    setCartItems((prev) => prev.filter((ci) => ci.product.id !== id));
  }, []);

  const updateQuantity = useCallback((id: number, quantity: number) => {
    if (quantity <= 0) {
      setCartItems((prev) => prev.filter((ci) => ci.product.id !== id));
    } else {
      setCartItems((prev) =>
        prev.map((ci) => ci.product.id === id ? { ...ci, quantity } : ci)
      );
    }
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  return (
    <CartContext.Provider value={{
      cartItems,
      cartCount: cartItems.length,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
